import { useState } from 'react';
import { useRouter } from 'src/routes/hooks';
import { m, useTransform, useMotionValue } from 'framer-motion';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Dialog from '@mui/material/Dialog';
import SvgIcon from '@mui/material/SvgIcon';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import ListItemText from '@mui/material/ListItemText';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';

import { fToNow } from 'src/utils/format-time';

import { handleNotificationAction } from 'src/actions/notifications';

import { useTranslate } from 'src/locales';

import { Label } from 'src/components/label';
import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';

import { notificationIcons } from './icons';

// ----------------------------------------------------------------------

export type NotificationAction = {
  type: 'approve' | 'reject' | 'request_info' | 'view_details' | 'retry';
  label: string;
  variant: 'primary' | 'danger' | 'secondary' | 'default';
  endpoint?: string;
  requiresNote?: boolean;
};

export type NotificationItemProps = {
  notification: {
    id: string;
    type: string;
    title: string;
    category: string;
    isUnRead: boolean;
    avatarUrl: string | null;
    createdAt: string | number | null;
    priority?: 'high' | 'medium' | 'low';
    targetRole?: 'admin' | 'dealer' | 'distributor';
    metadata?: Record<string, any>;
    actions?: NotificationAction[];
  };
  onMarkAsRead?: (id: string) => void;
  onDelete?: (id: string) => void;
};

const readerContent = (data: string) => (
  <Box
    dangerouslySetInnerHTML={{ __html: data }}
    sx={{
      '& p': { m: 0, typography: 'body2' },
      '& a': { color: 'inherit', textDecoration: 'none' },
      '& strong': { typography: 'subtitle2' },
    }}
  />
);

const renderIcon = (type: string) =>
  ({
    order: notificationIcons.order,
    chat: notificationIcons.chat,
    mail: notificationIcons.mail,
    delivery: notificationIcons.delivery,
    transaction_success: notificationIcons.transaction_success,
    transaction_failed: notificationIcons.transaction_failed,
    high_value_transaction: notificationIcons.high_value_transaction,
    cash_overdue: notificationIcons.cash_overdue,
    cash_mismatch: notificationIcons.cash_mismatch,
    cash_reminder: notificationIcons.cash_reminder,
    kyc_pending: notificationIcons.kyc_pending,
    pickup_assigned: notificationIcons.pickup_assigned,
    pickup_completed: notificationIcons.pickup_completed,
    reconciliation: notificationIcons.reconciliation,
    unusual_activity: notificationIcons.unusual_activity,
    beneficiary_verified: notificationIcons.beneficiary_verified,
    // New notification types
    transaction_approval_required: notificationIcons.transaction_approval_required,
    credit_extension_request: notificationIcons.credit_extension_request,
    transaction_stuck: notificationIcons.transaction_stuck,
    transaction_approved: notificationIcons.transaction_approved,
    transaction_rejected: notificationIcons.transaction_rejected,
    info_requested: notificationIcons.info_requested,
    credit_approved: notificationIcons.credit_approved,
    credit_rejected: notificationIcons.credit_rejected,
    credit_low: notificationIcons.credit_low,
    limit_approaching: notificationIcons.limit_approaching,
    pickup_urgent: notificationIcons.pickup_urgent,
    pickup_batch: notificationIcons.pickup_batch,
  })[type];

export function NotificationItem({ notification, onMarkAsRead, onDelete }: NotificationItemProps) {
  const { t } = useTranslate('layout');

  const router = useRouter();
  const [isRemoving, setIsRemoving] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isActioned, setIsActioned] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState<NotificationAction | null>(null);
  const [dialogText, setDialogText] = useState('');
  const x = useMotionValue(0);

  // Swipe threshold
  const SWIPE_THRESHOLD = 100;

  // Background colors based on swipe direction and distance
  const leftBgColor = useTransform(
    x,
    [-200, -SWIPE_THRESHOLD, 0],
    ['#FF5630', '#FF8A65', '#ffffff']
  );
  const rightBgColor = useTransform(
    x,
    [0, SWIPE_THRESHOLD, 200],
    ['#ffffff', '#90CAF9', '#1976D2']
  );

  const handleDragEnd = (_: any, info: any) => {
    const offset = info.offset.x;

    if (offset < -SWIPE_THRESHOLD) {
      // Swipe left - Delete
      setIsRemoving(true);
      setTimeout(() => {
        onDelete?.(notification.id);
      }, 300);
    } else if (offset > SWIPE_THRESHOLD) {
      // Swipe right - Mark as read
      if (notification.isUnRead && onMarkAsRead) {
        onMarkAsRead(notification.id);
      }
      // Animate back
      x.set(0);
    } else {
      // Didn't swipe far enough - animate back
      x.set(0);
    }
  };

  const handleAction = async (action: NotificationAction) => {
    // If the action requires a note, show dialog
    if (action.requiresNote) {
      setDialogAction(action);
      setDialogOpen(true);
      return;
    }

    setIsActionLoading(true);
    try {
      const result = await handleNotificationAction(action, notification.metadata);

      if (result?.navigate) {
        router.push(result.navigate);
      } else {
        toast.success(t('notificationItem.actionCompleted', { action: action.label }));
      }

      // Delete the notification after successful action (approve/reject)
      // This makes sense because a new notification will be sent to the relevant party
      if (action.type === 'approve' || action.type === 'reject') {
        // Mark as actioned for visual feedback
        setIsActioned(true);
        // Add a small delay for UX so user sees the success message first
        setTimeout(() => {
          setIsRemoving(true);
          setTimeout(() => {
            onDelete?.(notification.id);
          }, 300);
        }, 500);
      } else {
        // For other actions, just mark as read
        if (notification.isUnRead) {
          onMarkAsRead?.(notification.id);
        }
      }
    } catch (error: any) {
      toast.error(error.message || t('notificationItem.actionFailed'));
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDialogSubmit = async () => {
    if (!dialogAction) return;

    setDialogOpen(false);
    setIsActionLoading(true);

    try {
      const result = await handleNotificationAction(dialogAction, notification.metadata, {
        reason: dialogText,
        message: dialogText,
      });

      if (result?.navigate) {
        router.push(result.navigate);
      } else {
        toast.success(t('notificationItem.actionCompleted', { action: dialogAction.label }));
      }

      // Delete the notification after successful action (approve/reject)
      // This makes sense because a new notification will be sent to the relevant party
      if (dialogAction.type === 'approve' || dialogAction.type === 'reject') {
        // Mark as actioned for visual feedback
        setIsActioned(true);
        // Add a small delay for UX so user sees the success message first
        setTimeout(() => {
          setIsRemoving(true);
          setTimeout(() => {
            onDelete?.(notification.id);
          }, 300);
        }, 500);
      } else {
        // For other actions, just mark as read
        if (notification.isUnRead) {
          onMarkAsRead?.(notification.id);
        }
      }
    } catch (error: any) {
      toast.error(error.message || t('notificationItem.actionFailed'));
    } finally {
      setIsActionLoading(false);
      setDialogText('');
      setDialogAction(null);
    }
  };

  const renderAvatar = () => (
    <ListItemAvatar>
      {notification.avatarUrl ? (
        <Avatar src={notification.avatarUrl} sx={{ bgcolor: 'background.neutral' }} />
      ) : (
        <Box
          sx={{
            width: 40,
            height: 40,
            display: 'flex',
            borderRadius: '50%',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.neutral',
          }}
        >
          <SvgIcon sx={{ width: 24, height: 24 }}>{renderIcon(notification.type)}</SvgIcon>
        </Box>
      )}
    </ListItemAvatar>
  );

  const getPriorityLabel = () => {
    if (!notification.priority) return null;

    const priorityConfig = {
      high: { label: t('notificationItem.priority.high'), color: 'error' as const },
      medium: { label: t('notificationItem.priority.medium'), color: 'warning' as const },
      low: { label: t('notificationItem.priority.low'), color: 'info' as const },
    };

    const config = priorityConfig[notification.priority];
    if (!config) return null;

    return (
      <Label
        variant="soft"
        color={config.color}
        sx={{ mr: 1, mb: 0.5, fontSize: '0.625rem', height: 20 }}
      >
        {config.label}
      </Label>
    );
  };

  const renderText = () => (
    <ListItemText
      primary={
        <>
          {getPriorityLabel()}
          {readerContent(notification.title)}
        </>
      }
      secondary={
        <>
          {fToNow(notification.createdAt)}
          <Box
            component="span"
            sx={{ width: 2, height: 2, borderRadius: '50%', bgcolor: 'currentColor' }}
          />
          {notification.category}
          {notification.targetRole && (
            <>
              <Box
                component="span"
                sx={{ width: 2, height: 2, borderRadius: '50%', bgcolor: 'currentColor' }}
              />
              <Box component="span" sx={{ textTransform: 'capitalize' }}>
                {notification.targetRole}
              </Box>
            </>
          )}
        </>
      }
      slotProps={{
        primary: {
          sx: { mb: 0.5 },
        },
        secondary: {
          sx: {
            gap: 0.5,
            display: 'flex',
            alignItems: 'center',
            typography: 'caption',
            color: 'text.disabled',
          },
        },
      }}
    />
  );

  const renderUnReadBadge = () =>
    notification.isUnRead && (
      <Box
        sx={{
          top: 26,
          width: 8,
          height: 8,
          right: 20,
          borderRadius: '50%',
          bgcolor: 'info.main',
          position: 'absolute',
        }}
      />
    );

  // Map action variant to MUI Button color and variant
  const getButtonProps = (action: NotificationAction) => {
    const variantMap: Record<string, { color?: any; variant: 'contained' | 'outlined' | 'text' }> =
      {
        primary: { color: 'primary', variant: 'contained' },
        danger: { color: 'error', variant: 'contained' },
        secondary: { color: 'inherit', variant: 'outlined' },
        default: { variant: 'outlined' },
      };

    return variantMap[action.variant] || { variant: 'outlined' };
  };

  // Dynamic action renderer based on notification.actions array
  const renderDynamicActions = () => {
    if (!notification.actions || notification.actions.length === 0) {
      return null;
    }

    // If actioned, show success message instead of buttons
    if (isActioned) {
      return (
        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Iconify icon="eva:checkmark-circle-2-fill" sx={{ color: 'success.main' }} />
          <Typography variant="body2" color="success.main">
            {t('notificationItem.actionCompletedSimple')}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ gap: 1, mt: 1.5, display: 'flex', flexWrap: 'wrap' }}>
        {notification.actions.map((action, index) => (
          <Button
            key={index}
            size="small"
            {...getButtonProps(action)}
            disabled={isActionLoading}
            onClick={() => handleAction(action)}
          >
            {action.label}
          </Button>
        ))}
      </Box>
    );
  };

  const getPriorityBorderColor = () => {
    if (!notification.priority) return 'transparent';

    const priorityColors = {
      high: '#FF5630', // error.main
      medium: '#FFAB00', // warning.main
      low: '#00B8D9', // info.main
    };

    return priorityColors[notification.priority];
  };

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background indicators */}
        <m.div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: rightBgColor,
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '20px',
          }}
        >
          <Iconify icon="eva:checkmark-circle-2-fill" width={24} sx={{ color: 'white' }} />
          <Typography sx={{ ml: 1, color: 'white', fontWeight: 600 }}>
            {t('notificationItem.markAsRead')}
          </Typography>
        </m.div>

        <m.div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: leftBgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            paddingRight: '20px',
          }}
        >
          <Typography sx={{ mr: 1, color: 'white', fontWeight: 600 }}>
            {t('notificationItem.delete')}
          </Typography>
          <Iconify icon="eva:trash-2-fill" width={24} sx={{ color: 'white' }} />
        </m.div>

        {/* Swipeable notification */}
        <m.div
          drag="x"
          dragConstraints={{ left: -200, right: 200 }}
          dragElastic={0.2}
          onDragEnd={handleDragEnd}
          style={{ x }}
          animate={{
            opacity: isRemoving ? 0 : 1,
            x: isRemoving ? -400 : undefined,
          }}
          transition={{
            opacity: { duration: 0.3 },
            x: { duration: 0.3 },
          }}
        >
          <ListItemButton
            disableRipple
            sx={[
              (theme) => ({
                p: 2.5,
                alignItems: 'flex-start',
                borderBottom: `dashed 1px ${theme.vars.palette.divider}`,
                borderLeft: `3px solid ${getPriorityBorderColor()}`,
                position: 'relative',
                bgcolor: isActioned ? 'success.lighter' : 'background.paper',
                cursor: 'grab',
                opacity: isActioned ? 0.7 : 1,
                transition: 'all 0.3s ease',
                '&:active': {
                  cursor: 'grabbing',
                },
              }),
            ]}
          >
            {renderUnReadBadge()}
            {renderAvatar()}

            <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
              {renderText()}
              {/* Dynamic actions based on notification.actions array */}
              {renderDynamicActions()}
            </Box>
          </ListItemButton>
        </m.div>
      </Box>

      {/* Dialog for actions requiring note */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogAction?.type === 'reject' && t('notificationItem.dialog.rejectTitle')}
          {dialogAction?.type === 'request_info' && t('notificationItem.dialog.requestInfoTitle')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={
              dialogAction?.type === 'reject'
                ? t('notificationItem.dialog.rejectLabel')
                : t('notificationItem.dialog.requestInfoLabel')
            }
            fullWidth
            multiline
            rows={3}
            value={dialogText}
            onChange={(e) => setDialogText(e.target.value)}
            placeholder={
              dialogAction?.type === 'reject'
                ? t('notificationItem.dialog.rejectPlaceholder')
                : t('notificationItem.dialog.requestInfoPlaceholder')
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('notificationItem.dialog.cancel')}</Button>
          <Button
            onClick={handleDialogSubmit}
            variant="contained"
            disabled={!dialogText.trim()}
            color={dialogAction?.type === 'reject' ? 'error' : 'primary'}
          >
            {dialogAction?.label || t('notificationItem.dialog.submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
